import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Building2, Coins, Cpu, ExternalLink, PieChart, Wrench } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import StatusBadge from "@/components/shared/StatusBadge";
import { CHART } from "@/pages/admin/compliance/analytics/components/primitives";
import UsageStat from "@/components/usage/UsageStat";
import UsageByUserTable from "@/components/usage/UsageByUserTable";
import FeatureTable from "@/components/usage/FeatureTable";
import TokensCard from "@/components/usage/TokensCard";
import ToolUsageCard from "@/components/usage/ToolUsageCard";
import { fmt, fmtPct, pctOf, tenantName } from "@/components/usage/format";
import TenantUsageSkeleton from "./TenantUsageSkeleton";

/**
 * One tenant, seen by a SuperAdmin — everything a tenant admin sees of their own
 * usage, plus the two things only this seat can add: the engineering detail, and
 * the fleet the tenant sits in.
 *
 * The report cards are the *same components* the tenant page renders. That is
 * the point: when a SuperAdmin and a tenant admin discuss a figure, they are
 * looking at one screen, not two that resemble each other.
 *
 * Share of fleet is the one number added to the headline band. It is the only
 * question this seat can answer that the tenant's own page cannot.
 */
const TenantUsageView = ({ tenant, data, loading, searchLoading, search, onSearchChange, fleetTokens, onBack }) => {
  const summary = data?.summary;
  const tokens = summary?.tokens ?? {};
  const callDetail = summary?.llmCallDetail ?? null;
  const features = summary?.features ?? [];
  const uses = (summary?.modules ?? []).reduce((sum, mod) => sum + Number(mod.uses ?? 0), 0);
  const share = pctOf(tokens.totalTokens, fleetTokens);

  return (
    <>
      <Card className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-3">
          <Button
            onClick={onBack}
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border border-border-primary p-0 text-text-secondary transition-colors hover:bg-background-primary hover:text-text-primary"
            aria-label="Back to all tenants"
            title="Back to all tenants"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Building2 className="h-5 w-5 flex-shrink-0 text-text-secondary" />
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-text-primary">{tenantName(tenant?.name, tenant?.slug, tenant?.id)}</p>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-text-secondary">
              {tenant?.slug && <span className="truncate">{tenant.slug}</span>}
              {tenant?.dbMode && (
                <span className="rounded-full bg-tone-slate-soft px-2 py-0.5 text-tone-slate">
                  {tenant.dbMode === "dedicated" ? "dedicated database" : "shared database"}
                </span>
              )}
              {/* One pill, not two. `StatusBadge` puts Deleted ahead of
                  Inactive, which is what a soft-deleted tenant needs: it is
                  always inactive as well, and "Inactive" beside "Deleted"
                  would only make the reader ask which one counts. Nothing is
                  drawn for a healthy tenant — a green Active pill on every row
                  says nothing and hides the two rows that matter. */}
              {(tenant?.isDeleted || tenant?.isActive === false) && <StatusBadge size="sm" isActive={tenant?.isActive} isDeleted={tenant?.isDeleted} />}
            </div>
          </div>
        </div>

        {/* Not for a deleted tenant: the tenant-details endpoint filters
            `is_deleted`, so the link is a dead end that answers "not found".
            The usage figures on this page stand on their own — they are read
            from usage rows, which outlive the tenant. */}
        {tenant?.id && !tenant?.isDeleted && (
          <Link
            to={`/super-admin/tenant-details/${tenant.id}`}
            className="flex flex-shrink-0 items-center gap-1.5 self-start rounded-lg border border-border-primary px-2.5 py-1.5 text-xs text-text-secondary transition-colors hover:bg-background-primary hover:text-text-primary sm:self-auto"
          >
            Tenant details
            <ExternalLink className="h-3.5 w-3.5" />
          </Link>
        )}
      </Card>

      {/* `!data` rather than `loading && !data`: the fetch starts in an effect,
          one paint after the tenant changes, and in that gap an empty report
          would flash under the new tenant's name. */}
      {!data ? (
        <TenantUsageSkeleton />
      ) : (
        // A window or depth change keeps the figures on screen and dims them,
        // rather than throwing the reader back to a skeleton for numbers that
        // are about to look almost the same.
        <div className={`flex flex-col gap-4 transition-opacity ${loading ? "opacity-70" : ""}`}>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <UsageStat label="Tool uses" value={uses} accent={CHART.green} icon={Wrench} />
            <UsageStat label="Total tokens" value={tokens.totalTokens} accent={CHART.indigo} icon={Coins} />
            <UsageStat
              label="Share of fleet"
              display={fmtPct(share)}
              accent={CHART.amber}
              icon={PieChart}
              exact={`${fmt(tokens.totalTokens)} of ${fmt(fleetTokens)} fleet tokens`}
              hint={fleetTokens ? `of ${fmt(fleetTokens)} fleet tokens in this window` : "No fleet tokens in this window"}
            />
            {callDetail ? (
              <UsageStat
                label="LLM calls"
                value={callDetail.calls}
                accent={CHART.violet}
                icon={Cpu}
                hint={`${fmt(callDetail.cachedCalls)} more served from cache · ${fmt(callDetail.failedCalls)} failed`}
              />
            ) : (
              <UsageStat label="Input tokens" value={tokens.inputTokens} accent={CHART.blue} icon={Coins} hint={`${fmt(tokens.outputTokens)} output`} />
            )}
          </div>

          <ToolUsageCard modules={summary?.modules ?? []} emptyHint="This tenant ran no tools in this period." />
          <TokensCard tokens={tokens} callDetail={callDetail} features={features} />
          <FeatureTable features={features} />
          <UsageByUserTable data={data?.byUser} search={search} onSearchChange={onSearchChange} loading={searchLoading} />
        </div>
      )}
    </>
  );
};

export default TenantUsageView;
